/**
 * Shared website form fields (title, URL, description, and categorization).
 */

import { useEffect, useState, type CSSProperties } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Globe } from "lucide-react";
import { toast } from "sonner";
import { type WebsiteFormData } from "@/utils/websiteFormUtils";
import {
  WebsiteCategorizationFields,
  type WebsiteCategorizationLabels,
} from "./WebsiteCategorizationFields";

const requiredAsteriskStyle = { color: "var(--primary)" } as const;

const renderRequiredLabel = (label: string) => {
  const starIndex = label.indexOf("*");
  if (starIndex === -1) return label;
  const before = label.slice(0, starIndex);
  const after = label.slice(starIndex + 1);
  return (
    <>
      {before}
      <span style={requiredAsteriskStyle}>*</span>
      {after}
    </>
  );
};

interface WebsiteFormFieldsProps<T extends WebsiteFormData> {
  formData: T;
  setFormData: React.Dispatch<React.SetStateAction<T>>;
  t: (key: string) => string;
  language: string;
  isSubmitting?: boolean;
  showValidation: boolean;
  fieldErrors: {
    title: boolean;
    url: boolean;
    description: boolean;
  };
  setFieldErrors: React.Dispatch<
    React.SetStateAction<{
      title: boolean;
      url: boolean;
      description: boolean;
    }>
  >;
  urlErrorKey?: string | null;
  onUrlChange?: () => void;
  titleInputId: string;
  urlInputId: string;
  descriptionInputId: string;
  addressInputId: string;
  titleLabel: string;
  titlePlaceholder: string;
  urlLabel: string;
  urlPlaceholder: string;
  urlHint?: string;
  descriptionLabel: string;
  descriptionPlaceholder: string;
  showDescriptionCount?: boolean;
  maxTitleLen: number;
  maxUrlLen: number;
  maxDescriptionLen: number;
  maxAddressLen: number;
  maxZipLen: number;
  primaryTextStyle?: CSSProperties;
  secondaryTextStyle?: CSSProperties;
  errorTextStyle?: CSSProperties;
  iconStyle?: CSSProperties;
  aiFeatureEnabled?: boolean;
  categoryLabelText?: string;
  categoryFieldId?: string;
  regionFieldId?: string;
  showCategoryRequiredError?: boolean;
  showRegionRequiredError?: boolean;
  categoryRequiredErrorText?: string;
  regionRequiredErrorText?: string;
  addressLabelText?: string;
  regionLabels?: WebsiteCategorizationLabels;
  resetKey?: number;
}

export function WebsiteFormFields<T extends WebsiteFormData>({
  formData,
  setFormData,
  t,
  language,
  isSubmitting = false,
  showValidation,
  fieldErrors,
  setFieldErrors,
  urlErrorKey,
  onUrlChange,
  titleInputId,
  urlInputId,
  descriptionInputId,
  addressInputId,
  titleLabel,
  titlePlaceholder,
  urlLabel,
  urlPlaceholder,
  urlHint,
  descriptionLabel,
  descriptionPlaceholder,
  showDescriptionCount = true,
  maxTitleLen,
  maxUrlLen,
  maxDescriptionLen,
  maxAddressLen,
  maxZipLen,
  primaryTextStyle,
  secondaryTextStyle,
  errorTextStyle,
  iconStyle,
  aiFeatureEnabled = true,
  categoryLabelText,
  categoryFieldId,
  regionFieldId,
  showCategoryRequiredError = false,
  showRegionRequiredError = false,
  categoryRequiredErrorText,
  regionRequiredErrorText,
  addressLabelText,
  regionLabels,
  resetKey,
}: WebsiteFormFieldsProps<T>) {
  const [wasTitleTooLong, setWasTitleTooLong] = useState(false);
  const [wasUrlTooLong, setWasUrlTooLong] = useState(false);
  const [wasDescriptionTooLong, setWasDescriptionTooLong] = useState(false);

  useEffect(() => {
    setWasTitleTooLong(false);
    setWasUrlTooLong(false);
    setWasDescriptionTooLong(false);
  }, [resetKey]);

  const titleTooLong = formData.title.length > maxTitleLen;
  const urlTooLong = formData.url.length > maxUrlLen;
  const descriptionTooLong = formData.description.length > maxDescriptionLen;

  const titleInvalid = (showValidation && fieldErrors.title) || titleTooLong;
  const urlInvalid =
    (showValidation && fieldErrors.url) || Boolean(urlErrorKey) || urlTooLong;
  const descriptionInvalid =
    (showValidation && fieldErrors.description) || descriptionTooLong;

  const urlErrorText = urlTooLong
    ? t("websiteSuggest.errorUrlTooLong").replace("{max}", String(maxUrlLen))
    : urlErrorKey
      ? t(urlErrorKey)
      : showValidation && fieldErrors.url
        ? t("websiteSuggest.errorUrlRequired")
        : null;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={titleInputId} style={primaryTextStyle}>
            {renderRequiredLabel(titleLabel)}
          </Label>
          <Input
            id={titleInputId}
            placeholder={titlePlaceholder}
            value={formData.title}
            onChange={(e) => {
              const value = e.target.value;
              setFormData((prev) => ({ ...prev, title: value }));

              const tooLong = value.length > maxTitleLen;
              if (tooLong && !wasTitleTooLong) {
                toast.error(
                  t("websiteSuggest.errorTitleTooLong").replace(
                    "{max}",
                    String(maxTitleLen)
                  )
                );
              }
              setWasTitleTooLong(tooLong);

              if (showValidation) {
                setFieldErrors((prev) => ({ ...prev, title: !value.trim() }));
              }
            }}
            aria-invalid={titleInvalid ? true : undefined}
            disabled={isSubmitting}
          />

          {showValidation && fieldErrors.title && (
            <p className="text-xs font-semibold" style={errorTextStyle}>
              {t("websiteSuggest.errorTitleRequired")}
            </p>
          )}

          {titleTooLong && (
            <p className="text-xs font-semibold" style={errorTextStyle}>
              {t("websiteSuggest.errorTitleTooLong").replace(
                "{max}",
                String(maxTitleLen)
              )}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor={urlInputId} style={primaryTextStyle}>
            {renderRequiredLabel(urlLabel)}
          </Label>
          <div className="relative">
            <Globe
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={iconStyle}
            />
            <Input
              id={urlInputId}
              type="text"
              placeholder={urlPlaceholder}
              value={formData.url}
              onChange={(event) => {
                const value = event.target.value;
                setFormData((prev) => ({ ...prev, url: value }));
                onUrlChange?.();

                const tooLong = value.length > maxUrlLen;
                if (tooLong && !wasUrlTooLong) {
                  toast.error(
                    t("websiteSuggest.errorUrlTooLong").replace(
                      "{max}",
                      String(maxUrlLen)
                    )
                  );
                }
                setWasUrlTooLong(tooLong);

                if (showValidation) {
                  setFieldErrors((prev) => ({ ...prev, url: !value.trim() }));
                }
              }}
              aria-invalid={urlInvalid ? true : undefined}
              className="pl-9"
              disabled={isSubmitting}
            />
          </div>

          {urlInvalid && urlErrorText && (
            <p className="text-xs font-semibold" style={errorTextStyle}>
              {urlErrorText}
            </p>
          )}

          {urlHint && (
            <p className="text-xs" style={secondaryTextStyle}>
              {urlHint}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={descriptionInputId} style={primaryTextStyle}>
          {renderRequiredLabel(descriptionLabel)}
        </Label>
        <Textarea
          id={descriptionInputId}
          rows={4}
          placeholder={descriptionPlaceholder}
          value={formData.description}
          onChange={(e) => {
            const value = e.target.value;
            setFormData((prev) => ({ ...prev, description: value }));

            if (showValidation) {
              setFieldErrors((prev) => ({ ...prev, description: !value.trim() }));
            }

            const tooLong = value.length > maxDescriptionLen;
            if (tooLong && !wasDescriptionTooLong) {
              toast.error(
                t("websiteSuggest.errorDescriptionTooLong").replace(
                  "{max}",
                  String(maxDescriptionLen)
                )
              );
            }
            setWasDescriptionTooLong(tooLong);
          }}
          aria-invalid={descriptionInvalid ? true : undefined}
          disabled={isSubmitting}
        />

        {showValidation && fieldErrors.description && (
          <p className="text-xs font-semibold" style={errorTextStyle}>
            {t("websiteSuggest.errorDescriptionRequired")}
          </p>
        )}

        {descriptionTooLong && (
          <p className="text-xs font-semibold" style={errorTextStyle}>
            {t("websiteSuggest.errorDescriptionTooLong").replace(
              "{max}",
              String(maxDescriptionLen)
            )}
          </p>
        )}

        {showDescriptionCount && (
          <p className="text-xs" style={secondaryTextStyle}>
            {t("websiteSuggest.charactersCount").replace(
              "{count}",
              formData.description.length.toString()
            )}
          </p>
        )}
      </div>

      <WebsiteCategorizationFields
        formData={formData}
        setFormData={setFormData}
        t={t}
        language={language}
        addressInputId={addressInputId}
        maxTitleLen={maxTitleLen}
        maxDescriptionLen={maxDescriptionLen}
        maxAddressLen={maxAddressLen}
        maxZipLen={maxZipLen}
        isSubmitting={isSubmitting}
        primaryTextStyle={primaryTextStyle}
        secondaryTextStyle={secondaryTextStyle}
        errorTextStyle={errorTextStyle}
        aiFeatureEnabled={aiFeatureEnabled}
        categoryLabelText={categoryLabelText}
        categoryFieldId={categoryFieldId}
        regionFieldId={regionFieldId}
        showCategoryRequiredError={showCategoryRequiredError}
        showRegionRequiredError={showRegionRequiredError}
        categoryRequiredErrorText={categoryRequiredErrorText}
        regionRequiredErrorText={regionRequiredErrorText}
        addressLabelText={addressLabelText}
        regionLabels={regionLabels}
        resetKey={resetKey}
      />
    </>
  );
}
